import { X } from 'lucide-react';
import type { CSSProperties } from 'react';
import { getApiErrorMessage } from '../../lib/api';
import type { DashboardProductDetail } from '../../types/domain';
import { contrastTextColour } from './dashboardChartColours';

const dateFormatter = new Intl.DateTimeFormat('en-PK', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Asia/Karachi',
});

function formatDate(value: string) {
  return dateFormatter.format(new Date(`${value}T12:00:00+05:00`));
}

function targetValue(value: string, unit: string) {
  const formatted = Number(value).toLocaleString('en-PK', { maximumFractionDigits: 3 });
  return unit === 'PKR' ? `PKR ${formatted}` : `${formatted} ${unit}`;
}

export function ProductSalesDetailModal({
  detail,
  error,
  isLoading,
  onClose,
}: {
  detail: DashboardProductDetail | undefined;
  error: unknown;
  isLoading: boolean;
  onClose: () => void;
}) {
  const maximumUnits = Math.max(
    0,
    ...(detail?.stores.flatMap((store) => store.daily_sales.map((cell) => cell.accepted_units)) ?? []),
  );
  const heatColour = detail?.product_theme_color || detail?.brand_theme_color || '#2f8065';

  return (
    <div className="confirmation-backdrop store-sales-detail-backdrop" role="presentation">
      <section aria-labelledby="product-sales-detail-title" aria-modal="true" className="confirmation-dialog store-sales-detail-dialog" role="dialog">
        <button aria-label="Close product sales detail" className="icon-button confirmation-close" onClick={onClose} type="button">
          <X size={19} />
        </button>
        {isLoading ? (
          <div aria-live="polite" className="store-sales-detail-loading">
            <span className="button-spinner" /> Loading product sales detail…
          </div>
        ) : error ? (
          <div className="store-sales-detail-loading">
            <p className="form-error" role="alert">{getApiErrorMessage(error)}</p>
          </div>
        ) : detail ? (
          <>
            <header className="store-sales-detail-heading product-sales-detail-heading">
              <div>
                <p className="eyebrow">Product detail</p>
                <h2 id="product-sales-detail-title">{detail.name}</h2>
                <p>{detail.brand_name} · {detail.sku}</p>
                <div className="store-sales-detail-target">
                  <span>Product target achieved</span>
                  {detail.target_attainment.target_count ? (
                    <>
                      <strong>{Number(detail.target_attainment.percentage).toFixed(1)}%</strong>
                      <div>
                        {detail.target_attainment.summaries.map((summary) => (
                          <small key={summary.metric}>
                            {targetValue(summary.achieved_value, summary.display_unit)} / {targetValue(summary.target_value, summary.display_unit)}
                          </small>
                        ))}
                      </div>
                    </>
                  ) : <small>No product-specific target is configured.</small>}
                </div>
              </div>
            </header>

            <section aria-labelledby="product-sales-heatmap-title" className="store-sales-detail-heatmap-section">
              <div>
                <h3 id="product-sales-heatmap-title">Daily store sales</h3>
                <p>Accepted units by store for the selected reporting period, ordered by sales.</p>
              </div>
              {detail.stores.length ? (
                <div className="store-sales-detail-heatmap-scroll">
                  <table className="store-sales-detail-heatmap">
                    <thead>
                      <tr>
                        <th scope="col">Store</th>
                        {detail.dates.map((date) => <th key={date} scope="col">{formatDate(date)}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {detail.stores.map((store) => {
                        const salesByDate = new Map(store.daily_sales.map((cell) => [cell.date, cell]));
                        return (
                          <tr key={store.id}>
                            <th scope="row">
                              <strong>{store.name}</strong>
                              <small>{store.city}</small>
                            </th>
                            {detail.dates.map((date) => {
                              const cell = salesByDate.get(date);
                              const units = cell?.accepted_units ?? 0;
                              const intensity = maximumUnits ? units / maximumUnits : 0;
                              return (
                                <td key={date}>
                                  <span
                                    aria-label={`${store.name} on ${formatDate(date)}: ${units.toLocaleString('en-PK')} accepted units`}
                                    className="store-sales-detail-heatmap-cell"
                                    style={{
                                      '--heat-share': `${12 + intensity * 76}%`,
                                      '--heat-colour': heatColour,
                                      color: intensity >= 0.78 ? contrastTextColour(heatColour) : 'var(--ink)',
                                    } as CSSProperties}
                                    title={cell
                                      ? `${units.toLocaleString('en-PK')} accepted units · PKR ${Number(cell.accepted_value).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`
                                      : 'No accepted sales'}
                                  >
                                    {units ? units.toLocaleString('en-PK') : '—'}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact store-sales-detail-empty">
                  <h3>No campaign stores in this scope</h3>
                  <p>Adjust the filters or attach stores to the selected campaign.</p>
                </div>
              )}
            </section>
          </>
        ) : null}
      </section>
    </div>
  );
}
